import { c } from 'ttag';

import { PermissionDomain } from '../../graphql/__generated__/schema';
import { PortalModulesQuery } from '../../graphql/__generated__/types';

export type PortalModuleRef = NonNullable<PortalModulesQuery['portalModules']>[number];

export interface ClassifiableRight {
    module?: string | null;
    domain?: PermissionDomain | null;
}

export interface RightsGroup<R extends ClassifiableRight> {
    key: string;
    label: string;
    // `undefined` on the module-less groups: "delivered" says nothing about administration.
    delivered?: boolean;
    // The group exists because rights did not fit the referential. Never true today.
    anomaly?: boolean;
    permissions: R[];
}

const ADMIN_KEY = '__admin__';
const UNCLASSED_KEY = '__unclassed__';

// Labels and order come from `portalModules`, never from this file. Module groups are
// returned even when empty, so the referential is rendered whole; the module-less families
// only when something falls into them.
export const buildRightsGroups = <R extends ClassifiableRight>(
    rights: readonly R[],
    modules: readonly PortalModuleRef[]
): RightsGroup<R>[] => {
    const inMenuOrder = [...modules].sort((left, right) => left.sortOrder - right.sortOrder);
    const named = new Set(inMenuOrder.map((module) => module.code));

    const groups: RightsGroup<R>[] = inMenuOrder.map((module) => ({
        key: module.code,
        label: module.label,
        delivered: module.delivered,
        permissions: rights.filter((right) => right.module === module.code),
    }));

    // A right pointing at a module the referential does not name needs its own group,
    // so it is not silently dropped. Sorted by code for a stable order.
    const orphanCodes = [
        ...new Set(
            rights
                .map((right) => right.module)
                .filter((code): code is string => Boolean(code) && !named.has(code as string))
        ),
    ].sort();
    for (const code of orphanCodes) {
        groups.push({
            key: code,
            label: c('Rights matrix').t`Unknown module (${code})`,
            anomaly: true,
            permissions: rights.filter((right) => right.module === code),
        });
    }

    // Module-less rights: `domain` names the family, it does not form it. A module-less
    // right that is not administration lands in the anomaly group rather than under a
    // heading that lies.
    const moduleLess = rights.filter((right) => !right.module);
    const unclassed = moduleLess.filter((right) => right.domain !== PermissionDomain.Admin);
    const administration = moduleLess.filter((right) => right.domain === PermissionDomain.Admin);

    if (unclassed.length > 0) {
        groups.push({
            key: UNCLASSED_KEY,
            label: c('Rights matrix').t`Rights outside the referential`,
            anomaly: true,
            permissions: unclassed,
        });
    }

    if (administration.length > 0) {
        groups.push({
            key: ADMIN_KEY,
            label: c('Rights matrix').t`Administration`,
            permissions: administration,
        });
    }

    return groups;
};
